import { SweepFeature } from '@kb3d/models/classes/features/sweep-feature';
import { Token } from '@kb3d/models/classes/token';
import { FeatureRenderer, SubmeshVertexDataMap } from './feature';
import {
    SweepGeometryDependencyData,
    SweepGeometryProcessor,
    SweepGeometryWorkerPayload,
} from './geometry-processor/sweep-geometry';
import { isSketchNode } from '@kb3d/models/classes/meshes/mesh-utilities';
import { axisToVector3, parseSketchGeometry } from '@kb3d/view/modules/sketching/util';
import { eAxis } from '@kb3d/models/classes/enums';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { SpaceNode } from '@kb3d/models/classes/meshes/space-node';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { SketchNode } from '@kb3d/models/classes/meshes/sketch-node';
import { SubmeshVertexData } from '@kb3d/view/helpers/geometry-helper';

export class SweepRenderer extends FeatureRenderer<SweepFeature> {
    modelToken = Token.SweepFeature;
    processor = new SweepGeometryProcessor(this.scene);

    async makeWorkerPayload(feature: SweepFeature, geometryData: SubmeshVertexDataMap) {
        const payload = await super.makeWorkerPayload(feature, geometryData);

        const sketch = feature._parent && isSketchNode(feature._parent) ? feature._parent : undefined;
        const sketchNormal = sketch ? axisToVector3(sketch.normal) : Vector3.Zero();
        if (sketch && sketch.normal !== eAxis.z) {
            sketchNormal.scaleInPlace(-1);
        }

        if (!feature.path) {
            return payload;
        }

        const targetPathNode = feature._manager?.getById<SketchNode>(feature.path);
        if (!targetPathNode) {
            return payload;
        }
        const pathMesh = this.viewer.getRendererNode(targetPathNode);
        const thisMesh = this.viewer.getRendererNode(feature._parent!);
        if (!pathMesh || !thisMesh || !(pathMesh instanceof Mesh)) {
            return payload;
        }

        const pathVertexData = SubmeshVertexData.ExtractFromMesh(pathMesh);
        const { segments } = parseSketchGeometry(pathVertexData);
        if (segments.length === 0) {
            return payload;
        }
        const pathPoints = segments[0];
        const pathPointsForTransfer = new Float32Array(pathPoints.length * 3);
        for (let i = 0; i < pathPoints.length; i++) {
            pathPointsForTransfer[i * 3] = pathPoints[i].x;
            pathPointsForTransfer[i * 3 + 1] = pathPoints[i].y;
            pathPointsForTransfer[i * 3 + 2] = pathPoints[i].z;
        }

        const featureData: SweepGeometryWorkerPayload = {
            sketchNormal: { x: sketchNormal.x, y: sketchNormal.y, z: sketchNormal.z },
            pathPoints: pathPointsForTransfer,
            pathMeshTransform: pathMesh.computeWorldMatrix(true).toArray(),
            thisMeshTransform: thisMesh.computeWorldMatrix(true).toArray(),
        };
        payload.featureData = featureData;
        payload.transfer = [pathPointsForTransfer.buffer];
        return payload;
    }

    updateMeshGeometry(
        feature: SweepFeature,
        vertexData: SubmeshVertexData,
        vertexDataChanges: Partial<SubmeshVertexData>
    ) {
        const newVertexData = super.updateMeshGeometry(feature, vertexData, vertexDataChanges);
        const meshDynamicId = feature._parent?._dynamicId || '';
        const sketch = feature._parent && isSketchNode(feature._parent) ? feature._parent : undefined;
        const sketchNormal = sketch ? axisToVector3(sketch.normal) : Vector3.Zero();
        if (sketch && sketch.normal !== eAxis.z) {
            sketchNormal.scaleInPlace(-1);
        }

        if (!feature.path) {
            return newVertexData;
        }

        const targetPathNode = feature._manager?.getById<SketchNode>(feature.path);
        if (!targetPathNode) {
            return newVertexData;
        }
        const pathMesh = this.viewer.getRendererNode(targetPathNode);
        const thisMesh = this.viewer.getRendererNode(feature._parent!);
        if (!pathMesh || !thisMesh || !(pathMesh instanceof Mesh)) {
            return newVertexData;
        }

        const pathVertexData = SubmeshVertexData.ExtractFromMesh(pathMesh);
        const { segments } = parseSketchGeometry(pathVertexData);
        if (segments.length === 0) {
            return newVertexData;
        }
        const pathPoints = segments[0];

        const dependencyData: SweepGeometryDependencyData = {
            sketchNormal,
            pathPoints,
            pathMeshTransform: pathMesh.computeWorldMatrix(true),
            thisMeshTransform: thisMesh.computeWorldMatrix(true),
        };
        return this.processor.updateMeshGeometry(feature, vertexData, vertexDataChanges, {
            meshDynamicId,
            data: dependencyData,
        });
    }

    update3dInternal() {}
}
